/** SUNTRONIX ONE — ONE CONTINUITY & SUCCESSION CENTRE (MY ONE → Continuity).
 *  Orchestration & reference layer. Grant-based access only. */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy, Search, FileDown, ShieldAlert } from "lucide-react";
import {
  Overview, BusinessArea, PersonalArea, ChecklistSection, KeyPeople,
  ResponsibilityMap, Renewals, FinanceArea, InsuranceSection, AssetsArea,
  Playbook, MissingSection, HistorySection, AccessSettings,
} from "./OneContinuitySections";
import { SensitiveWatermark } from "@/components/app/SensitiveWatermark";

export default function OneContinuity() {
  const [access, setAccess] = useState(null);
  const [denied, setDenied] = useState(false);
  const [section, setSection] = useState("OVERVIEW");
  const [refreshKey, setRefreshKey] = useState(0);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const box = useRef(null);

  useEffect(() => {
    api.get("/continuity/access")
      .then(({ data }) => (data.business || data.personal) ? setAccess(data) : setDenied(true))
      .catch(() => setDenied(true));
  }, []);
  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(() => {
      api.get("/continuity/search", { params: { q } })
        .then(({ data }) => setResults(data.groups || []));
    }, 350);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const close = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      if (e.type === "mousedown" && box.current?.contains(e.target)) return;
      setResults(null);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => { window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", close); };
  }, []);

  if (denied) return (
    <div className="max-w-md mx-auto mt-20 text-center space-y-3" data-testid="cont-denied">
      <ShieldAlert className="h-10 w-10 mx-auto text-slate-300" />
      <div className="text-sm text-slate-500">Continuity access is granted explicitly by the owner.</div>
    </div>);
  if (!access) return null;

  const AREAS = [
    "OVERVIEW",
    ...(access.business ? ["BUSINESS CONTINUITY"] : []),
    ...(access.personal ? ["PERSONAL / FAMILY"] : []),
    "IMPORTANT DOCUMENTS", "KEY PEOPLE",
    ...(access.business ? ["RESPONSIBILITY MAP"] : []),
    "RENEWALS", "FINANCE", "INSURANCE",
    ...(access.business ? ["ASSETS"] : []),
    "IMPORTANT INSTRUCTIONS", "MISSING INFORMATION", "HISTORY",
    ...(access.admin ? ["ACCESS & SETTINGS"] : []),
  ];
  const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");
  const bump = () => setRefreshKey((k) => k + 1);
  const common = { access, refreshKey, bump, goto: setSection };
  const downloadIndex = async () => {
    try {
      const res = await api.get("/continuity/index/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "continuity-index.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Index generation failed"); }
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-4 relative" data-testid="one-continuity-page">
      <SensitiveWatermark area="PERSONAL_CONTINUITY" />
      <header className="flex flex-wrap items-center gap-3 bg-emerald-950 text-white rounded-xl px-4 py-3">
        <div className="h-10 w-10 rounded-lg bg-emerald-400 text-emerald-950 grid place-items-center">
          <LifeBuoy className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="text-lg font-heading font-semibold tracking-wide">ONE CONTINUITY</div>
          <div className="text-[10.5px] text-emerald-200/80">
            So the business and family can keep going — {access.profile?.replace(/_/g, " ") || ""} access
          </div>
        </div>
        <div className="relative" ref={box}>
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-emerald-300" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="cont-search"
              placeholder='Ask: "Who is our CA?", "HDFC statements"…'
              className="text-[12px] bg-emerald-900/70 border border-emerald-800 rounded-md pl-8 pr-3 py-1.5 w-80 placeholder:text-emerald-400/60" />
          {results && (
            <div className="absolute z-40 top-10 left-0 w-[430px] max-h-96 overflow-y-auto bg-white text-slate-800 rounded-lg shadow-xl border border-slate-200 p-2"
                data-testid="cont-search-results">
              {results.length === 0 && <div className="text-[11px] text-slate-400 p-3">No matches you're authorised to see.</div>}
              {results.map((g) => (
                <div key={g.group} className="mb-2">
                  <div className="text-[9px] uppercase tracking-wide text-slate-400 px-1">{g.group}</div>
                  {g.items.map((it, i) => (
                    <button key={i} data-testid={`cont-sr-${slug(g.group)}-${i}`}
                        onClick={() => {
                          setQ(""); setResults(null);
                          if (it.path.startsWith("internal:")) {
                            const map = { people: "KEY PEOPLE", responsibilities: "RESPONSIBILITY MAP",
                              "info-index": "BUSINESS CONTINUITY", insurance: "INSURANCE",
                              playbook: "IMPORTANT INSTRUCTIONS" };
                            setSection(map[it.path.split(":")[1]] || "OVERVIEW");
                          } else window.location.href = it.path;
                        }}
                        className="w-full text-left text-[11px] px-2 py-1.5 rounded hover:bg-slate-100">
                      {it.label}</button>))}
                </div>))}
            </div>)}
        </div>
        <button onClick={downloadIndex} data-testid="cont-generate-index"
            className="text-[11px] px-2.5 py-1.5 rounded-md bg-emerald-400 text-emerald-950 font-semibold inline-flex items-center gap-1">
          <FileDown className="h-3 w-3" /> Generate Continuity Index</button>
      </header>

      <div className="flex gap-4 items-start">
        <nav className="w-52 shrink-0 space-y-0.5" data-testid="cont-nav">
          {AREAS.map((s) => (
            <button key={s} onClick={() => setSection(s)} data-testid={`cont-nav-${slug(s)}`}
                className={"w-full text-left text-[11px] px-2.5 py-2 rounded-md " +
                  (section === s ? "bg-emerald-950 text-white font-semibold"
                                 : "text-slate-600 hover:bg-slate-100")}>
              {s}</button>))}
        </nav>
        <div className="flex-1 min-w-0">
          {section === "OVERVIEW" && <Overview {...common} />}
          {section === "BUSINESS CONTINUITY" && <BusinessArea {...common} />}
          {section === "PERSONAL / FAMILY" && <PersonalArea {...common} />}
          {section === "IMPORTANT DOCUMENTS" && <ChecklistSection {...common} />}
          {section === "KEY PEOPLE" && <KeyPeople {...common} />}
          {section === "RESPONSIBILITY MAP" && <ResponsibilityMap {...common} />}
          {section === "RENEWALS" && <Renewals {...common} />}
          {section === "FINANCE" && <FinanceArea {...common} />}
          {section === "INSURANCE" && <InsuranceSection {...common} />}
          {section === "ASSETS" && <AssetsArea {...common} />}
          {section === "IMPORTANT INSTRUCTIONS" && <Playbook {...common} />}
          {section === "MISSING INFORMATION" && <MissingSection {...common} />}
          {section === "HISTORY" && <HistorySection {...common} />}
          {section === "ACCESS & SETTINGS" && <AccessSettings {...common} />}
        </div>
      </div>
    </div>
  );
}
